import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import moment from 'moment';

const API_URL = "https://ims.holdthechild.net/api";

// Helper function to format the status for display
const formatStatus = (status) => {
    switch (status) {
        case 'Pending':
            return 'Pending (HR Review)';
        case 'HR_Recommended':
            return 'HR Recommended (Pending Director Review)';
        case 'Program_Director_Approved':
            return 'Approved';
        case 'Rejected':
            return 'Rejected';
        case 'Cancelled':
            return 'Cancelled';
        default:
            return status;
    }
};

const getStatusColor = (status) => {
    switch (status) {
        case 'Approved':
        case 'Program_Director_Approved':
            return 'bg-green-100 text-green-800';
        case 'Rejected':
        case 'Cancelled':
            return 'bg-red-100 text-red-800';
        case 'Pending':
        case 'HR_Recommended':
        default:
            return 'bg-yellow-100 text-yellow-800';
    }
};

const LeaveRequestForm = ({ staffId: propStaffId, token: propToken }) => {
    const token = propToken || localStorage.getItem('token');
    const id = propStaffId || localStorage.getItem('id');

    const [policyList, setPolicyList] = useState([]);
    const [policyMap, setPolicyMap] = useState({});

    const [formData, setFormData] = useState({
        staffId: id || '',
        firstName: '',
        lastName: '',
        email: '',
        department: '',
        position: '',
        typeOfLeave: '',
        otherLeaveNote: '',
        numberRequested: '',
        startDate: '',
        endDate: '',
        reason: '',
        replacementPerson: '',
        contactNumber: '',
    });

    const [staffList, setStaffList] = useState([]); // all staff in department
    const [filteredStaff, setFilteredStaff] = useState([]); // filtered staff by search
    const [showDropdown, setShowDropdown] = useState(false);

    const [submitting, setSubmitting] = useState(false);
    const [message, setMessage] = useState(null);
    const [leaveConflict, setLeaveConflict] = useState(false);
    const [remainingDays, setRemainingDays] = useState(null);
    const [myLeaveRequests, setMyLeaveRequests] = useState([]); // New state for employee's leave history

    const today = new Date().toISOString().split('T')[0];
    const dropdownRef = useRef();

    // Fetch user profile and existing leave requests
    useEffect(() => {
        if (!id || !token) return;

        const fetchData = async () => {
            try {
                // Fetch profile
                const profileRes = await axios.get(`${API_URL}/staff/${id}/me`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const { firstName, lastName, email, department, jobTitle } = profileRes.data.data || {};

                // Update formData with the fetched profile data.
                // Note: This is an async operation, so the state won't be
                // updated immediately. We must use the fetched 'email'
                // directly for the next API call to ensure it's available.
                setFormData((prev) => ({
                    ...prev,
                    firstName: firstName || '',
                    lastName: lastName || '',
                    email: email || '',
                    department: department || '',
                    position: jobTitle || '',
                    staffId: id,
                }));

                // Fetch existing leave requests using the email from the profile response.
                // We are using `email` directly from the `profileRes` object
                // to ensure the filter has a value.
                const leaveRes = await axios.get(`${API_URL}/leave-request?email=${encodeURIComponent(email)}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setMyLeaveRequests(leaveRes.data.data || []);
            } catch (err) {
                console.error('Error fetching data', err);
            }
        };

        fetchData();
    }, [id, token]);


    // Fetch staff list filtered by department once department is known
    useEffect(() => {
        if (!formData.department || !token) return;

        axios.get(`${API_URL}/staff?department=${encodeURIComponent(formData.department)}`, {
            headers: { Authorization: `Bearer ${token}` }
        })
            .then(res => {
                const staffMembers = res.data.data || [];
                const filteredStaff = staffMembers.filter(member => member._id !== id);
                setStaffList(filteredStaff);
                setFilteredStaff(filteredStaff);
            })
            .catch((err) => {
                console.error('Error fetching staff list by department', err);
                setStaffList([]);
                setFilteredStaff([]);
            });
    }, [formData.department, token]);

    // Fetch leave policies
    useEffect(() => {
        if (!token) return;

        axios
            .get(`${API_URL}/staff/leave-policy`, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then((res) => {
                const data = res.data || [];
                setPolicyList(data);
                const map = data.reduce((acc, item) => {
                    acc[item.name] = item;
                    return acc;
                }, {});
                setPolicyMap(map);
            })
            .catch((err) => {
                console.error('Error fetching leave policy', err);
                setPolicyList([]);
                setPolicyMap({});
            });
    }, [token]);

    // Check leave conflicts
    useEffect(() => {
        if (!formData.typeOfLeave || formData.typeOfLeave === 'Sick Leave') {
            setLeaveConflict(false);
            return;
        }
        if (!token || !formData.email) return;

        const checkConflict = async () => {
            try {
                const res = await axios.get(
                    `${API_URL}/leave-request?email=${encodeURIComponent(formData.email)}`,
                    {
                        headers: { Authorization: `Bearer ${token}` },
                    }
                );

                const requests = res.data.data || [];
                const now = new Date();
                const hasPending = requests.some(
                    (r) => (r.status === 'Pending' || r.status === 'HR_Recommended' || r.status === 'Program_Director_Approved') && new Date(r.endDate) >= now
                );
                setLeaveConflict(hasPending);
            } catch (err) {
                console.error('Error checking leave conflict', err);
            }
        };

        checkConflict();
    }, [formData.typeOfLeave, formData.email, token]);


    // Fetch used leave days and calculate remaining
    useEffect(() => {
        const fetchUsed = async () => {
            const type = formData.typeOfLeave;
            const policy = policyMap[type];
            if (!policy || !token || !id) return;

            try {
                const res = await axios.get(
                    `${API_URL}/staff/${id}/leave-usage?type=${type}`,
                    {
                        headers: { Authorization: `Bearer ${token}` },
                    }
                );

                const used = res.data.used || 0;
                if (policy.type !== 'discretion' && policy.type !== 'unlimited') {
                    setRemainingDays(policy.days - used);
                } else {
                    setRemainingDays(null);
                }
            } catch (err) {
                console.error('Error fetching leave usage', err);
            }
        };

        if (formData.typeOfLeave) {
            fetchUsed();
        } else {
            setRemainingDays(null);
        }
    }, [formData.typeOfLeave, token, id, policyMap]);

    // Handle form input changes
    const handleChange = (e) => {
        const { name, value } = e.target;
        setMessage(null);

        if (name === 'replacementPerson') {
            const filtered = staffList.filter((staff) => {
                const fullName = `${staff.firstName} ${staff.lastName}`.toLowerCase();
                return fullName.includes(value.toLowerCase());
            });
            setFilteredStaff(filtered);
            setShowDropdown(true);
        }

        setFormData((prev) => {
            let updated = { ...prev, [name]: value };

            if (name === 'numberRequested') {
                let num = parseInt(value);
                if (remainingDays != null && num > remainingDays) {
                    num = remainingDays;
                }
                updated.numberRequested = isNaN(num) ? '' : num.toString();
            }

            if (
                (name === 'startDate' || name === 'numberRequested') &&
                updated.startDate &&
                updated.numberRequested
            ) {
                const start = new Date(updated.startDate);
                const days = parseInt(updated.numberRequested);
                if (!isNaN(days)) {
                    let end = new Date(start);
                    let count = 0;
                    while (count < days) {
                        if (end.getDay() !== 0 && end.getDay() !== 6) count++;
                        if (count < days) end.setDate(end.getDate() + 1);
                    }
                    updated.endDate = end.toISOString().split('T')[0];
                }
            }

            if (name === 'endDate' && updated.startDate && updated.endDate) {
                const start = new Date(updated.startDate);
                const end = new Date(updated.endDate);
                let count = 0;
                let current = new Date(start);
                while (current <= end) {
                    if (current.getDay() !== 0 && current.getDay() !== 6) count++;
                    current.setDate(current.getDate() + 1);
                }
                updated.numberRequested = count.toString();
            }

            return updated;
        });
    };

    const handleSelectReplacement = (staff) => {
        setFormData((prev) => ({
            ...prev,
            replacementPerson: `${staff.firstName} ${staff.lastName}`,
        }));
        setShowDropdown(false);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const getMinEndDate = () => {
        if (!formData.startDate) return '';
        const start = new Date(formData.startDate);
        start.setDate(start.getDate() + 1);
        return start.toISOString().split('T')[0];
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage(null);

        if (leaveConflict && formData.typeOfLeave !== 'Sick Leave') {
            setMessage({
                type: 'error',
                text: 'You already have an active or pending leave request. Wait for it to be processed.',
            });
            return;
        }

        if (!formData.typeOfLeave) return setMessage({ type: 'error', text: 'Select type of leave' });
        if (formData.typeOfLeave === 'Other Paid Leave' && !formData.otherLeaveNote.trim())
            return setMessage({ type: 'error', text: 'Specify other paid leave' });
        if (!formData.startDate || !formData.endDate)
            return setMessage({ type: 'error', text: 'Select start and end dates' });
        if (new Date(formData.endDate) <= new Date(formData.startDate))
            return setMessage({ type: 'error', text: 'End date must be after start date' });
        if (!formData.numberRequested || formData.numberRequested < 1)
            return setMessage({ type: 'error', text: 'Invalid number of days' });

        if (remainingDays != null && parseInt(formData.numberRequested) > remainingDays) {
            return setMessage({
                type: 'error',
                text: `You only have ${remainingDays} days remaining for ${formData.typeOfLeave}.`,
            });
        }

        setSubmitting(true);

        try {
            const payload = {
                id,
                typeOfLeave: formData.typeOfLeave,
                otherLeaveNote: formData.otherLeaveNote,
                hoursOrDays: 'Days',
                numberRequested: formData.numberRequested,
                startDate: formData.startDate,
                endDate: formData.endDate,
                reason: formData.reason,
                replacementPerson: formData.replacementPerson,
                contactNumber: formData.contactNumber,
            };

            await axios.post(`${API_URL}/leave-request`, payload, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            setMessage({ type: 'success', text: 'Leave request submitted successfully.' });
            setFormData((prev) => ({
                ...prev,
                typeOfLeave: '',
                otherLeaveNote: '',
                numberRequested: '',
                startDate: '',
                endDate: '',
                reason: '',
                replacementPerson: '',
                contactNumber: '',
            }));
            setLeaveConflict(true);
            setRemainingDays(null);

            // Re-fetch leave requests to update the history table
            // This is the correct way to get the updated list of leave requests
            // after a new one has been submitted.
            const leaveRes = await axios.get(`${API_URL}/leave-request?email=${encodeURIComponent(formData.email)}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setMyLeaveRequests(leaveRes.data.data || []);
        } catch (err) {
            console.error(err);
            setMessage({ type: 'error', text: err.response?.data?.message || 'Failed to submit request.' });
        } finally {
            setSubmitting(false);
        }
    };

    // Helper to get the relevant comment based on the current status
const getDecisionComment = (leave) => {
    if (!leave.approvals || leave.approvals.length === 0) {
        return '-';
    }

    // If Rejected, find the specific rejection reason (HR or Director)
    if (leave.status === 'Rejected') {
        const rejector = leave.approvals.find(a => a.status === 'Rejected');
        return rejector ? rejector.comment : 'No reason provided';
    }

    // If finally Approved, look for the Director's comment
    if (leave.status === 'Program_Director_Approved') {
        const director = leave.approvals.find(a => a.role === 'program_director');
        return director ? director.comment : 'Approved by Director';
    }

    // If Recommended by HR, show HR's comment
    if (leave.status === 'HR_Recommended') {
        const hr = leave.approvals.find(a => a.role === 'hr_officer');
        return hr ? hr.comment : 'HR Recommended';
    }

    return '-';
};

    return (
        <div className="max-w-4xl mx-auto p-6 bg-white rounded-xl shadow-md max-h-[90vh] overflow-auto">
            <h2 className="text-xl font-bold mb-4 text-orange-500">Leave Request Form</h2>

            {message && (
                <div
                    className={`mb-4 p-3 rounded ${message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}
                >
                    {message.text}
                </div>
            )}

            {leaveConflict && formData.typeOfLeave && formData.typeOfLeave !== 'Sick Leave' && (
                <div className="mb-4 p-3 rounded bg-yellow-100 text-yellow-800 border border-yellow-300">
                    You already have a pending or approved leave request.
                </div>
            )}

            <form onSubmit={handleSubmit}>
                <div className="mb-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label className="block font-semibold mb-1">Full Name</label>
                        <input
                            type="text"
                            value={`${formData.firstName} ${formData.lastName}`}
                            readOnly
                            className="w-full border px-3 py-2 rounded bg-gray-100"
                        />
                    </div>
                    <div>
                        <label className="block font-semibold mb-1">Email</label>
                        <input
                            type="email"
                            value={formData.email}
                            readOnly
                            className="w-full border px-3 py-2 rounded bg-gray-100"
                        />
                    </div>
                    <div>
                        <label className="block font-semibold mb-1">Department</label>
                        <input
                            type="text"
                            value={formData.department}
                            readOnly
                            className="w-full border px-3 py-2 rounded bg-gray-100"
                        />
                    </div>
                    <div>
                        <label className="block font-semibold mb-1">Position</label>
                        <input
                            type="text"
                            value={formData.position}
                            readOnly
                            className="w-full border px-3 py-2 rounded bg-gray-100"
                        />
                    </div>
                </div>

                <div className="mb-4">
                    <label className="block font-semibold mb-1" htmlFor="typeOfLeave">
                        Type of Leave <span className="text-red-500">*</span>
                    </label>
                    <select
                        id="typeOfLeave"
                        name="typeOfLeave"
                        value={formData.typeOfLeave}
                        onChange={handleChange}
                        required
                        className="w-full border px-3 py-2 rounded"
                    >
                        <option value="">-- Select --</option>
                        {policyList.map((item) => (
                            <option key={item.name} value={item.name}>
                                {item.name}
                            </option>
                        ))}
                    </select>

                    {remainingDays != null && (
                        <p className="mt-2 text-orange-600 font-semibold">
                            You have <strong>{remainingDays}</strong> days left out of{' '}
                            <strong>{policyMap[formData.typeOfLeave]?.days || '-'}</strong> for <em>{formData.typeOfLeave}</em>.
                        </p>
                    )}

                    {formData.typeOfLeave === 'Other Paid Leave' && (
                        <input
                            type="text"
                            name="otherLeaveNote"
                            value={formData.otherLeaveNote}
                            onChange={handleChange}
                            placeholder="Specify other paid leave"
                            className="mt-2 w-full border px-3 py-2 rounded"
                            required
                        />
                    )}
                </div>

                <div className="mb-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                        <label className="block font-semibold mb-1" htmlFor="numberRequested">
                            Number of Days <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="number"
                            id="numberRequested"
                            name="numberRequested"
                            value={formData.numberRequested}
                            onChange={handleChange}
                            min="1"
                            max={remainingDays || undefined}
                            required
                            className="w-full border px-3 py-2 rounded"
                        />
                    </div>

                    <div>
                        <label className="block font-semibold mb-1" htmlFor="startDate">
                            Start Date <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="date"
                            id="startDate"
                            name="startDate"
                            value={formData.startDate}
                            onChange={handleChange}
                            min={today}
                            required
                            className="w-full border px-3 py-2 rounded"
                        />
                    </div>

                    <div>
                        <label className="block font-semibold mb-1" htmlFor="endDate">
                            End Date <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="date"
                            id="endDate"
                            name="endDate"
                            value={formData.endDate}
                            onChange={handleChange}
                            min={formData.startDate || today}
                            required
                            className="w-full border px-3 py-2 rounded"
                        />
                    </div>
                </div>

                <div className="mb-4">
                    <label className="block font-semibold mb-1" htmlFor="reason">
                        Reason
                    </label>
                    <textarea
                        id="reason"
                        name="reason"
                        value={formData.reason}
                        onChange={handleChange}
                        rows="3"
                        className="w-full border px-3 py-2 rounded"
                        placeholder="Compulsory"
                    />
                </div>

                <div className="mb-4 relative" ref={dropdownRef}>
                    <label className="block font-semibold mb-1" htmlFor="replacementPerson">
                        Replacement Person
                    </label>
                    <input
                        type="text"
                        id="replacementPerson"
                        name="replacementPerson"
                        value={formData.replacementPerson}
                        onChange={handleChange}
                        autoComplete="off"
                        className="w-full border px-3 py-2 rounded"
                        placeholder="Start typing a name..."
                        onFocus={() => setShowDropdown(true)}
                    />
                    {showDropdown && filteredStaff.length > 0 && (
                        <ul className="absolute z-10 w-full bg-white border border-gray-300 rounded max-h-48 overflow-auto mt-1 shadow-lg">
                            {filteredStaff.map((staff) => (
                                <li
                                    key={staff.id}
                                    onClick={() => handleSelectReplacement(staff)}
                                    className="px-3 py-2 cursor-pointer hover:bg-orange-100"
                                >
                                    {staff.firstName} {staff.lastName}
                                </li>
                            ))}
                        </ul>
                    )}
                    {showDropdown && filteredStaff.length === 0 && (
                        <div className="absolute z-10 w-full bg-white border border-gray-300 rounded p-2 mt-1 text-gray-500">
                            No matches found.
                        </div>
                    )}
                </div>

                <div className="mb-6">
                    <label className="block font-semibold mb-1" htmlFor="contactNumber">
                        Contact Number
                    </label>
                    <input
                        type="tel"
                        id="contactNumber"
                        name="contactNumber"
                        value={formData.contactNumber}
                        onChange={handleChange}
                        className="w-full border px-3 py-2 rounded"
                        placeholder="Your contact number"
                    />
                </div>

                <button
                    type="submit"
                    disabled={submitting}
                    className={`w-full py-3 rounded text-white font-bold ${submitting ? 'bg-orange-300 cursor-not-allowed' : 'bg-orange-500 hover:bg-orange-600'
                        }`}
                >
                    {submitting ? 'Submitting...' : 'Submit Request'}
                </button>
            </form>

            <hr className="my-8" />

            {/* Existing Leave Requests Table */}
            <div className="mt-8">
                <h3 className="text-xl font-bold text-gray-700 mb-4">My Existing Leave Requests</h3>
                <div className="overflow-x-auto">
                    <table className="min-w-full text-sm divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left font-medium text-gray-500 uppercase tracking-wider">Leave Type</th>
                                <th className="px-6 py-3 text-left font-medium text-gray-500 uppercase tracking-wider">Date Range</th>
                                <th className="px-6 py-3 text-left font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                                <th className="px-6 py-3 text-left font-medium text-gray-500 uppercase tracking-wider">Reviewer Comments</th>

                                <th className="px-6 py-3 text-left font-medium text-gray-500 uppercase tracking-wider">Status</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {myLeaveRequests.length === 0 ? (
                                <tr>
                                    <td colSpan="4" className="px-6 py-4 text-center text-gray-500">No leave requests found.</td>
                                </tr>
                            ) : (
                                myLeaveRequests.map((leave) => (
                                    <tr key={leave._id}>
                                        <td className="px-6 py-4 whitespace-nowrap">{leave.typeOfLeave}</td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {moment(leave.startDate).format("MMM D, YYYY")} - {moment(leave.endDate).format("MMM D, YYYY")}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {leave.numberRequested} {leave.hoursOrDays}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate" title={getDecisionComment(leave)}>
                                            {getDecisionComment(leave)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(leave.status)}`}>
                                                {formatStatus(leave.status)}
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default LeaveRequestForm;